import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiCommonService } from '@api-services/api-common.service';
import { COMPANY_API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import { FiltersParams, HttpHelper } from '@helpers/http.helper';
import { JsonId } from '@interfaces/json-id';
import { PersonConfigFile, PersonConfigFileJson } from '@models/person-config-file';
import { PersonTotal, PersonTotalJson } from '@models/person-total';
import { User, UserJson } from '@models/user';
import { FileSaverData } from '@services/file.service';

/**
 * Person Api Service.
 */
@Injectable({
  providedIn: 'root'
})
export class PersonApiService extends ApiCommonService<User> {
  readonly resourceUrl = `${COMPANY_API_ROUTE_BASE}/persons`;
  readonly moralPersonsResourceUrl = `${COMPANY_API_ROUTE_BASE}/moral_persons`;
  readonly personConfigFilesResourceUrl = `${COMPANY_API_ROUTE_BASE}/person_config_files`;

  fromJson(json: UserJson): User {
    return User.fromJson(json);
  }

  toJson(model: User): Partial<UserJson> {
    return User.toJson(model);
  }

  @ApiAlertError()
  getPersons(searchTerms?: string, sort?: Sort, filters?: FiltersParams, companyId?: number): Observable<User[]> {
    const url = companyId ? `api/v1/expert/companies/${companyId}/persons` : `${this.resourceUrl}`;
    let params: HttpParams = HttpHelper.setQParam(searchTerms);
    if (sort) {
      params = HttpHelper.setSortParam(sort, params);
    }
    if (filters) {
      params = HttpHelper.setParams(filters, params);
    }

    return super.getAll({ params }, url);
  }

  getPhysicalPersons(filters?: FiltersParams, companyId?: number): Observable<User[]> {
    return this.getPersons(null, null, { moral_persons: false, ...filters }, companyId);
  }

  @ApiAlertError([403])
  getPersonsTotals(): Observable<PersonTotal> {
    const url = `${this.resourceUrl}/totals`;

    return this.http
      .get<PersonTotalJson>(url)
      .pipe(map((personTotalJson: PersonTotalJson) => PersonTotal.fromJson(personTotalJson)));
  }

  @ApiAlertError()
  createMoralPerson(user: User): Observable<User> {
    return super.create(user, this.moralPersonsResourceUrl);
  }

  @ApiAlertError()
  updateMoralPerson(user: User): Observable<User> {
    const url = `${this.moralPersonsResourceUrl}/${user.id}`;

    return super.update(user.id, user, url);
  }

  @ApiAlertError()
  removeMoralPerson(userId: number): Observable<unknown> {
    const url = `${this.moralPersonsResourceUrl}/${userId}`;

    return super.delete(userId, url);
  }

  @ApiAlertError()
  addPersonConfigFile(file: PersonConfigFile, userId: number): Observable<PersonConfigFile> {
    const url = this.personConfigFilesResourceUrl;

    const formData = new FormData();
    formData.append('type', file.type);
    if (file.title) {
      formData.append('title', file.title);
    }
    formData.append('file', file.file);
    formData.append('user_id', userId.toString());

    return this.http
      .post<PersonConfigFileJson>(url, formData)
      .pipe(map((json: PersonConfigFileJson) => PersonConfigFile.fromJson(json)));
  }

  @ApiAlertError()
  getPersonConfigFile(fileId: number): Observable<FileSaverData> {
    const url = `${this.personConfigFilesResourceUrl}/${fileId}/file`;

    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });
    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  deletePersonConfigFile(fileId: number): Observable<void> {
    const url = `${this.personConfigFilesResourceUrl}/${fileId}`;

    return this.http.delete<void>(url);
  }

  @ApiAlertError()
  addPersonConfig(userId: number): Observable<JsonId> {
    const url = this.personConfigFilesResourceUrl;

    return this.http.post<JsonId>(url, {
      user_id: userId
    });
  }
}
